import { Link } from 'react-router-dom';
import type { IconType } from 'react-icons';
import { Card } from '@/components/ui/card';
import { cn } from '@/components/ui/utils';

/**
 * A single Quick Action tile on Home.
 *
 * Visually a soft-card with an icon circle, a label, and an optional helper
 * line. Entire tile is the click target, minimum 88px tall so touch targets
 * stay well above 44px.
 */
export interface QuickActionTileProps {
  to: string;
  icon: IconType;
  label: string;
  helper?: string;
  accent?: 'brand' | 'cta' | 'neutral';
  className?: string;
}

const ACCENT_STYLES: Record<NonNullable<QuickActionTileProps['accent']>, string> = {
  brand: 'bg-[var(--brand-light)] text-[var(--brand)]',
  cta: 'bg-[color-mix(in_oklab,var(--cta)_15%,transparent)] text-[var(--cta)]',
  neutral: 'bg-[var(--background)] text-[var(--text)]',
};

export function QuickActionTile({
  to,
  icon: Icon,
  label,
  helper,
  accent = 'brand',
  className,
}: QuickActionTileProps) {
  return (
    <Card
      className={cn(
        'p-0 overflow-hidden transition-all hover:shadow-md focus-within:ring-2 focus-within:ring-[var(--brand)]',
        className,
      )}
    >
      <Link
        to={to}
        className="flex flex-col gap-2 p-4 min-h-[112px] sm:min-h-[128px] focus-visible:outline-none"
        aria-label={helper ? `${label}. ${helper}` : label}
      >
        <span
          className={cn(
            'inline-flex items-center justify-center w-10 h-10 rounded-full',
            ACCENT_STYLES[accent],
          )}
          aria-hidden
        >
          <Icon size={22} />
        </span>
        <span className="font-semibold text-[14px] text-[var(--text)] leading-tight">
          {label}
        </span>
        {helper ? (
          <span className="text-[12px] text-[var(--text-muted)] leading-tight">{helper}</span>
        ) : null}
      </Link>
    </Card>
  );
}
